import { AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    AddVehicleComponent,
    AddVehicleDialogResponse,
} from '@polaris-customerweb/sharedcomponents.garage';
import {
    ImageCdnSrcDirective,
    IsMobileViewService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { get } from 'lodash-es';
import { Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AppState } from 'src/app/models/app-state';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { WINDOW } from 'src/app/state/app-config/factories/window.factory';
import { ThemeService } from 'src/app/state/theme/theme.service';
import { UserAuthService } from 'src/app/state/user-auth/user-auth.service';
import { changeSidenavState } from './actions/sidenav.actions';
import { getIsSidenavOpen } from './mobile-sidenav.reducer';

@Component({
    imports: [
        AnalyticsDataDirective,
        AsyncPipe,
        ImageCdnSrcDirective,
        MatButtonModule,
        RouterLink,
    ],
    selector: 'rdx-mobile-sidenav',
    styleUrls: ['./mobile-sidenav.component.scss'],
    templateUrl: './mobile-sidenav.component.html',
})
export class MobileSidenavComponent implements OnInit, OnDestroy {
    brand: string;
    homeUrl: string;
    isAuthenticated$: Observable<boolean>;
    isEpi: boolean;
    isEulaCurrent$ = new Observable<boolean>();
    isIndian = false;
    $isMobileView = this.isMobileViewService.$isMobileView;
    isOffRoad = false;
    isSidenavOpen = false;
    isSlingshot = false;
    mobileNavLogo = '';
    supportUrl: string;

    private isDestroyed = false;

    constructor(
        private configService: ConfigService,
        private localStorageService: LocalStorageService,
        private matDialog: MatDialog,
        private router: Router,
        private store: Store<AppState>,
        private themeService: ThemeService,
        private userAuthService: UserAuthService,
        private isMobileViewService: IsMobileViewService,
        @Inject(WINDOW) private window: Window
    ) {
        this.brand = configService.brand;
        this.isIndian = configService.isIndian;
        this.isSlingshot = configService.isSlingshot;
        this.isOffRoad = configService.isOffRoad;
        this.isEpi = this.configService.isEmbedded;
        if (this.isEpi) {
            this.supportUrl = this.themeService.getBrandLink(
                this.brand,
                'support'
            );
        }
        if (this.isEpi) {
            this.homeUrl = this.themeService.getBrandLink(this.brand, 'home');
        }
    }

    ngOnInit(): void {
        this.isAuthenticated$ = this.userAuthService.isAuthenticated$;

        if (!this.configService.isEpi) {
            this.setMetadata();
        }
        this.checkForLogoutRedirect();
        this.checkForPasswordChangeSuccess();
        // browserUpdate({
        //     notify: { i: -3, f: -4, o: -4, s: -2, c: -4 },
        // });

        this.store
            .select(getIsSidenavOpen)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((isSidenavOpen) => {
                this.isSidenavOpen = isSidenavOpen;
            });
    }

    closeSidenav(): void {
        this.store.dispatch(changeSidenavState());
    }

    logIn() {
        this.userAuthService.logIn();
    }

    logOut() {
        this.userAuthService.logOut();
    }

    addVehicle() {
        this.showCreateEditVehicle();
    }

    signUp() {
        this.userAuthService.logIn(undefined, undefined, 'signUp');
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    private showCreateEditVehicle() {
        this.matDialog
            .open<
                AddVehicleComponent,
                { initialState: string; vehicleId: string | undefined },
                AddVehicleDialogResponse
            >(AddVehicleComponent, {
                data: { initialState: 'start', vehicleId: undefined },
                height: this.isMobileViewService.$isMobileView()
                    ? '100%'
                    : undefined,
                width: this.isMobileViewService.$isMobileView()
                    ? '100%'
                    : undefined,
            })
            .afterClosed()
            .subscribe((res) => {
                if (res?.addedVehicle) {
                    void this.router.navigate(['maintenance']);
                }
            });
    }

    private checkForLogoutRedirect(): void {
        const currentLocationLogout = this.localStorageService.get(
            'currentLocationLogout'
        ) as { name: string } | null;
        if (currentLocationLogout?.name) {
            this.localStorageService.remove('currentLocationLogout');
            // this.state.go(currentLocationLogout.name);
        }
    }

    private checkForPasswordChangeSuccess(): void {
        const search = this.hash2obj(this.window.location.hash);
        const message = get(search, 'message');
        if (message && search.success) {
            this.localStorageService.set('urlMessage', message);
        }
    }

    private hash2obj(hash: string): Record<string, string> {
        return hash
            .split('&')
            .map((el) => el.split('='))
            .reduce((obj, entry) => {
                obj[entry[0]] = entry[1];
                return obj;
            }, {});
    }

    private setMetadata(): void {
        const canonicalEl = this.window.document.querySelector(
            'link[rel="canonical"]'
        );
        const ogUrlEl = this.window.document.querySelector(
            'meta[property="og:url"]'
        );
        let rootUrl: string;
        if (this.configService.isIndian) {
            rootUrl = 'https://ridecommand.indianmotorcycle.com';
        } else if (this.configService.isSlingshot) {
            rootUrl = 'https://ridecommand.slingshot.polaris.com';
        } else {
            rootUrl = 'https://ridecommand.polars.com';
        }
        const url = `${rootUrl}${this.window.location.pathname}`;
        canonicalEl?.setAttribute('href', url);
        ogUrlEl?.setAttribute('content', url);
    }
}
